import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, Eye, Users, TrendingUp, Calendar, ArrowRight, Crown, PieChart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Analytics = () => {
  const { user, loading, isBusinessPremium } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventAnalytics, setEventAnalytics] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingEvent, setLoadingEvent] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (!loading && user && !isBusinessPremium()) {
      toast.error('Analytics requires Business Premium membership');
      navigate('/membership');
    }
  }, [user, loading, navigate, isBusinessPremium]);

  useEffect(() => {
    if (user && isBusinessPremium()) {
      fetchDashboard();
    }
  }, [user]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`);
      setDashboard(response.data);
      if (response.data.events?.length > 0) {
        setSelectedEvent(response.data.events[0].id);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoadingDashboard(false);
    }
  };

  useEffect(() => {
    if (selectedEvent) {
      fetchEventAnalytics(selectedEvent);
    }
  }, [selectedEvent]);

  const fetchEventAnalytics = async (eventId) => {
    setLoadingEvent(true);
    try {
      const response = await axios.get(`${API}/events/${eventId}/analytics`);
      setEventAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching event analytics:', error);
    } finally {
      setLoadingEvent(false);
    }
  };

  const getJobFunctionLabel = (value) => {
    const labels = {
      physician: 'Physician/Doctor',
      nurse: 'Nurse (RN/LPN)',
      social_worker: 'Social Worker',
      administrator: 'Healthcare Administrator',
      attorney: 'Attorney/Legal',
      therapist: 'Therapist (PT/OT/ST)',
      care_manager: 'Care Manager',
      executive: 'Executive/C-Suite',
      sales_marketing: 'Sales/Marketing',
      consultant: 'Consultant',
      researcher: 'Researcher/Academic',
      policy_advocate: 'Policy/Advocacy',
      other: 'Other',
      unknown: 'Unknown'
    };
    return labels[value] || value;
  };

  const getCompanyTypeLabel = (value) => {
    const labels = {
      hospital: 'Hospital/Health System',
      nursing_home: 'Nursing Home/SNF',
      assisted_living: 'Assisted Living',
      home_care: 'Home Care Agency',
      hospice: 'Hospice',
      insurance: 'Insurance/Payer',
      pharma: 'Pharmaceutical',
      medical_device: 'Medical Device',
      tech_startup: 'Healthcare Tech/Startup',
      law_firm: 'Law Firm',
      consulting: 'Consulting Firm',
      nonprofit: 'Non-Profit/Advocacy',
      government: 'Government Agency',
      academic: 'Academic/Research',
      other: 'Other',
      unknown: 'Unknown'
    };
    return labels[value] || value;
  };

  if (loading || loadingDashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user || !isBusinessPremium()) return null;

  return (
    <div className="min-h-screen bg-secondary/30 py-12">
      <div className="container-custom max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-8 h-8 text-gold" />
            <h1 className="text-3xl font-semibold">Analytics Dashboard</h1>
          </div>
          <p className="text-muted-foreground">Track performance across all your events</p>
        </div>

        {/* Overview Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-10">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Events</p>
                  <p className="text-3xl font-semibold">{dashboard?.total_events || 0}</p>
                </div>
                <Calendar className="w-10 h-10 text-gold opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                  <p className="text-3xl font-semibold">{dashboard?.total_views || 0}</p>
                </div>
                <Eye className="w-10 h-10 text-gold opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Attendees</p>
                  <p className="text-3xl font-semibold">{dashboard?.total_attendees || 0}</p>
                </div>
                <Users className="w-10 h-10 text-gold opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-3xl font-semibold">{dashboard?.overall_conversion_rate || 0}%</p>
                </div>
                <TrendingUp className="w-10 h-10 text-gold opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {dashboard?.events?.length > 0 ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Events List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>Your Events</CardTitle>
                  <CardDescription>Select an event to view detailed analytics</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dashboard.events.map((event) => (
                    <button
                      key={event.id}
                      onClick={() => setSelectedEvent(event.id)}
                      className={`w-full text-left p-4 rounded-lg border transition-colors ${
                        selectedEvent === event.id 
                          ? 'border-gold bg-gold/10' 
                          : 'border-border hover:border-gold/50'
                      }`}
                      data-testid={`event-analytics-${event.id}`}
                    >
                      <p className="font-medium line-clamp-1">{event.title}</p>
                      <p className="text-sm text-muted-foreground">{format(parseISO(event.date), 'MMM d, yyyy')}</p>
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>{event.views} views</span>
                        <span>{event.total_attendees} attendees</span>
                        <span>{event.conversion_rate}% conv.</span>
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Event Details */}
            <div className="lg:col-span-2 space-y-6">
              {loadingEvent ? (
                <div className="flex justify-center py-20">
                  <div className="spinner" />
                </div>
              ) : eventAnalytics ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>{eventAnalytics.event_title}</CardTitle>
                      <CardDescription>Performance metrics</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="text-center p-4 bg-secondary/50 rounded-lg">
                          <Eye className="w-6 h-6 text-gold mx-auto mb-2" />
                          <p className="text-2xl font-semibold">{eventAnalytics.summary.total_views}</p>
                          <p className="text-sm text-muted-foreground">Total Views</p>
                          <p className="text-xs text-muted-foreground mt-1">{eventAnalytics.summary.unique_views} unique</p>
                        </div>
                        <div className="text-center p-4 bg-secondary/50 rounded-lg">
                          <Users className="w-6 h-6 text-gold mx-auto mb-2" />
                          <p className="text-2xl font-semibold">{eventAnalytics.summary.total_attendees}</p>
                          <p className="text-sm text-muted-foreground">Attendees</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {eventAnalytics.summary.total_rsvps} RSVPs, {eventAnalytics.summary.total_tickets} tickets
                          </p>
                        </div>
                        <div className="text-center p-4 bg-secondary/50 rounded-lg">
                          <TrendingUp className="w-6 h-6 text-gold mx-auto mb-2" />
                          <p className="text-2xl font-semibold">{eventAnalytics.summary.conversion_rate}%</p>
                          <p className="text-sm text-muted-foreground">Conversion Rate</p>
                          <p className="text-xs text-muted-foreground mt-1">Views → Registrations</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Audience Demographics */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <PieChart className="w-5 h-5 text-gold" />
                          Attendees by Job Function
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {Object.keys(eventAnalytics.attendee_demographics.by_job_function).length > 0 ? (
                          <div className="space-y-3">
                            {Object.entries(eventAnalytics.attendee_demographics.by_job_function)
                              .sort((a, b) => b[1] - a[1])
                              .map(([key, value]) => (
                                <div key={key} className="flex items-center justify-between">
                                  <span className="text-sm">{getJobFunctionLabel(key)}</span>
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="h-2 bg-gold rounded"
                                      style={{ 
                                        width: `${(value / eventAnalytics.summary.total_attendees) * 100}px`,
                                        minWidth: '20px'
                                      }}
                                    />
                                    <span className="text-sm text-muted-foreground w-8">{value}</span>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">No attendee data yet</p>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <PieChart className="w-5 h-5 text-gold" />
                          Attendees by Company Type
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {Object.keys(eventAnalytics.attendee_demographics.by_company_type).length > 0 ? (
                          <div className="space-y-3">
                            {Object.entries(eventAnalytics.attendee_demographics.by_company_type)
                              .sort((a, b) => b[1] - a[1])
                              .map(([key, value]) => (
                                <div key={key} className="flex items-center justify-between">
                                  <span className="text-sm">{getCompanyTypeLabel(key)}</span>
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="h-2 bg-gold rounded"
                                      style={{ 
                                        width: `${(value / eventAnalytics.summary.total_attendees) * 100}px`,
                                        minWidth: '20px'
                                      }}
                                    />
                                    <span className="text-sm text-muted-foreground w-8">{value}</span>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">No attendee data yet</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Views Timeline */}
                  {eventAnalytics.views_over_time.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Views Over Time</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-end gap-1 h-32">
                          {eventAnalytics.views_over_time.map((day, index) => {
                            const maxViews = Math.max(...eventAnalytics.views_over_time.map(d => d.views));
                            const height = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
                            return (
                              <div 
                                key={index} 
                                className="flex-1 bg-gold/20 hover:bg-gold/40 transition-colors rounded-t relative group"
                                style={{ height: `${Math.max(height, 5)}%` }}
                              >
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-primary text-primary-foreground text-xs px-2 py-1 rounded whitespace-nowrap">
                                  {day.date}: {day.views} views
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-2">
                          <span>{eventAnalytics.views_over_time[0]?.date}</span>
                          <span>{eventAnalytics.views_over_time[eventAnalytics.views_over_time.length - 1]?.date}</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="py-10 text-center">
                    <p className="text-muted-foreground">Select an event to view analytics</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <BarChart3 className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
              <h3 className="text-xl mb-2">No Events Yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first event to start tracking analytics
              </p>
              <Button 
                onClick={() => navigate('/create-event')}
                className="bg-gold text-primary hover:bg-gold/90"
                data-testid="create-event-analytics-btn"
              >
                Create Event
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Analytics;
